/**
 * High-performance utility to sanitize CSS rules and cloned DOM before running html2canvas.
 * html2canvas 1.4.1 only supports legacy color formats (rgb, rgba, hsl, hsla, hex, named colors).
 * Modern CSS functions such as color(srgb ...), color-mix(...), oklch(...), lab(...), etc.
 * cause html2canvas to throw: "Attempting to parse an unsupported color function".
 */

const helperCanvas = typeof document !== "undefined" ? document.createElement("canvas") : null;
const helperCtx = helperCanvas ? helperCanvas.getContext("2d") : null;

/**
 * Regex matching CSS Color 4 and Color 5 modern functions (including nested expressions).
 */
export const UNSUPPORTED_COLOR_FN_REGEX = /(?:color|color-mix|oklch|oklab|lab|lch|light-dark|hwb)\((?:[^)(]+|\((?:[^)(]+|\([^)(]*\))*\))*\)/gi;

/**
 * Fast substring test to avoid regex execution when not needed.
 */
export const QUICK_COLOR_TEST = /color\(|color-mix\(|oklch\(|oklab\(|lab\(|lch\(|light-dark\(|hwb\(/i;

/**
 * In-memory cache for parsed colors to make repeated lookups instant (O(1)).
 */
const colorCache = new Map<string, string>();

/**
 * Converts a modern CSS color function to standard rgb/rgba/hex using Canvas 2D context with caching.
 */
export const normalizeCssColor = (colorStr: string): string => {
  const cached = colorCache.get(colorStr);
  if (cached !== undefined) return cached;

  if (!helperCtx) return "rgba(0, 0, 0, 0)";

  try {
    helperCtx.fillStyle = "#000000";
    helperCtx.fillStyle = colorStr;
    const computed = helperCtx.fillStyle;
    const result = computed && !QUICK_COLOR_TEST.test(computed) ? computed : "rgba(0, 0, 0, 0)";
    colorCache.set(colorStr, result);
    return result;
  } catch {
    colorCache.set(colorStr, "rgba(0, 0, 0, 0)");
    return "rgba(0, 0, 0, 0)";
  }
};

/**
 * Replaces all unsupported color functions in a CSS string with supported equivalents.
 */
export const sanitizeCssText = (css: string): string => {
  if (!css || !QUICK_COLOR_TEST.test(css)) return css;
  return css.replace(UNSUPPORTED_COLOR_FN_REGEX, (match) => normalizeCssColor(match));
};

/**
 * Sanitizes the cloned document created by html2canvas before rasterization.
 */
export const sanitizeHtml2CanvasClone = (clonedDocument: Document): void => {
  try {
    // 1. Sanitize only <style> tags that actually contain modern color functions
    const styleElements = clonedDocument.querySelectorAll("style");
    styleElements.forEach((styleEl) => {
      const text = styleEl.textContent;
      if (text && QUICK_COLOR_TEST.test(text)) {
        styleEl.textContent = sanitizeCssText(text);
      }
    });

    // 2. Fast targeted selector for inline styles with modern colors
    const elementsWithStyle = clonedDocument.querySelectorAll<HTMLElement>(
      "[style*='color('], [style*='oklch('], [style*='color-mix('], [style*='lab('], [style*='lch(']",
    );
    elementsWithStyle.forEach((el) => {
      const styleAttr = el.getAttribute("style");
      if (styleAttr && QUICK_COLOR_TEST.test(styleAttr)) {
        el.setAttribute("style", sanitizeCssText(styleAttr));
      }
    });
  } catch (error) {
    console.warn("[sanitizeHtml2CanvasClone] Warning during DOM sanitization:", error);
  }
};

const isNearChannel = (value: number, target: number, tolerance = 12) => Math.abs(value - target) <= tolerance;

/**
 * html2canvas can emit a canvas taller than the visible section (cloned page min-height / 100vh).
 * Drop trailing empty rows so the PDF does not keep a blank first page.
 */
export const trimCanvasTrailingBlank = (
  canvas: HTMLCanvasElement,
  pageBackgroundRgb: [number, number, number] = [255, 255, 255],
): HTMLCanvasElement => {
  const context = canvas.getContext("2d");

  if (!context || canvas.width === 0 || canvas.height === 0) return canvas;

  const { width, height } = canvas;
  const { data } = context.getImageData(0, 0, width, height);
  const [pageRed, pageGreen, pageBlue] = pageBackgroundRgb;

  const rowIsBlank = (row: number) => {
    const offset = row * width * 4;

    for (let column = 0; column < width; column += 1) {
      const index = offset + column * 4;
      const alpha = data[index + 3];
      const red = data[index];
      const green = data[index + 1];
      const blue = data[index + 2];

      if (alpha < 8) continue;

      const matchesPage = isNearChannel(red, pageRed) && isNearChannel(green, pageGreen) && isNearChannel(blue, pageBlue);
      const matchesWhite = isNearChannel(red, 255) && isNearChannel(green, 255) && isNearChannel(blue, 255);

      if (!matchesPage && !matchesWhite) return false;
    }

    return true;
  };

  let lastContentRow = height - 1;

  while (lastContentRow > 0 && rowIsBlank(lastContentRow)) {
    lastContentRow -= 1;
  }

  const trimmedHeight = Math.min(height, lastContentRow + 3);

  if (trimmedHeight >= height - 2) return canvas;

  const trimmed = document.createElement("canvas");
  trimmed.width = width;
  trimmed.height = trimmedHeight;
  trimmed.getContext("2d")?.drawImage(canvas, 0, 0, width, trimmedHeight, 0, 0, width, trimmedHeight);

  return trimmed;
};

export const prepareHtml2CanvasClone = (clonedDocument: Document, clonedElement?: HTMLElement) => {
  clonedDocument.documentElement.style.height = "auto";
  clonedDocument.documentElement.style.minHeight = "0";
  clonedDocument.body.style.margin = "0";
  clonedDocument.body.style.padding = "0";
  clonedDocument.body.style.height = "auto";
  clonedDocument.body.style.minHeight = "0";
  clonedDocument.body.style.overflow = "visible";
  clonedDocument.body.style.backgroundColor = "transparent";

  if (clonedElement) {
    clonedDocument.body.replaceChildren(clonedElement);
    clonedElement.style.position = "static";
    clonedElement.style.top = "auto";
    clonedElement.style.left = "auto";
    clonedElement.style.right = "auto";
    clonedElement.style.bottom = "auto";
    clonedElement.style.height = "auto";
    clonedElement.style.minHeight = "0";
    clonedElement.style.maxHeight = "none";
    clonedElement.style.margin = "0";
    clonedElement.style.transform = "none";
  }

  sanitizeHtml2CanvasClone(clonedDocument);
};
